import { useEffect, useMemo, useRef, useState } from "react";
import { useGlobalSnackbar } from "../../context/SnackbarContext";
import { useSocket } from "../../context/SocketContext";
import { ENotificationType, INotification } from "../interfaces";

export interface IUseWatchBaseNotificationProps<TPayload> {
  notificationTypes: ENotificationType[];
  dissableSnackbarAlert?: boolean;
  onNotificationArrived?: (notification?: INotification<TPayload>) => void;
  buildSnackbarMessage?: (notification: INotification<TPayload>) => string;
}

const parseSocketNotification = <TPayload>(
  raw: unknown,
  eventType: ENotificationType,
): INotification<TPayload> | null => {
  if (!raw) return null;

  if (typeof raw === "string") {
    try {
      return parseSocketNotification<TPayload>(JSON.parse(raw), eventType);
    } catch {
      return null;
    }
  }

  if (typeof raw !== "object") return null;

  const payload = raw as INotification<TPayload>;
  const meta =
    typeof payload.meta === "string"
      ? (() => {
          try {
            return JSON.parse(payload.meta) as TPayload;
          } catch {
            return payload.meta;
          }
        })()
      : payload.meta;

  return {
    ...payload,
    type: payload.type ?? eventType,
    meta,
  };
};

export function useWatchBaseNotification<TPayload>({
  notificationTypes,
  dissableSnackbarAlert = false,
  onNotificationArrived,
  buildSnackbarMessage,
}: IUseWatchBaseNotificationProps<TPayload>): {
  lastNotification: INotification<TPayload> | null;
  clearLastNotification: () => void;
} {
  const { socket } = useSocket();
  const [lastNotification, setLastNotification] = useState<INotification<TPayload> | null>(null);
  const { showSnackbar } = useGlobalSnackbar();

  const dissableSnackbarAlertRef = useRef(dissableSnackbarAlert);
  const onNotificationArrivedRef = useRef(onNotificationArrived);
  const buildSnackbarMessageRef = useRef(buildSnackbarMessage);
  const showSnackbarRef = useRef(showSnackbar);

  useEffect(() => {
    dissableSnackbarAlertRef.current = dissableSnackbarAlert;
  }, [dissableSnackbarAlert]);

  useEffect(() => {
    onNotificationArrivedRef.current = onNotificationArrived;
  }, [onNotificationArrived]);

  useEffect(() => {
    buildSnackbarMessageRef.current = buildSnackbarMessage;
  }, [buildSnackbarMessage]);

  useEffect(() => {
    showSnackbarRef.current = showSnackbar;
  }, [showSnackbar]);

  const normalizedNotificationTypes = useMemo(() => {
    const types = Array.isArray(notificationTypes) ? notificationTypes : [notificationTypes];
    return Array.from(new Set(types));
  }, [notificationTypes]);

  const clearLastNotification = (): void => {
    setLastNotification(null);
  };

  useEffect(() => {
    if (!socket || normalizedNotificationTypes.length === 0) return;

    const handlers = normalizedNotificationTypes.map((eventType) => {
      const handler = (raw: unknown) => {
        const notification = parseSocketNotification<TPayload>(raw, eventType);
        if (!notification?.type) return;

        onNotificationArrivedRef.current?.(notification);
        setLastNotification(notification);

        if (!dissableSnackbarAlertRef.current) {
          const message = buildSnackbarMessageRef.current?.(notification) ?? "";
          if (message) {
            showSnackbarRef.current(message, "info");
          }
        }
      };

      socket.on(eventType, handler);
      return { eventType, handler };
    });

    return () => {
      handlers.forEach(({ eventType, handler }) => {
        socket.off(eventType, handler);
      });
    };
  }, [socket, normalizedNotificationTypes]);

  return { lastNotification, clearLastNotification };
}
